/**
 * PaymentMethodsPanel — Iter-160 · Phase 5 + Iter-162 · Phases 1/4.
 *
 * Full professional payment-method suite for the PM Billing & A/R
 * tab. Four methods, each in its own clearly labeled tab; when none
 * is active all four appear equally weighted (no ACH default).
 *
 *   • ACH Direct Debit (recurring autopay)     — Stripe Bank Setup.
 *   • Credit / Debit Card (recurring autopay)  — Inline Stripe Elements.
 *   • ACH One-Time (single transfer)           — Stripe PaymentIntent.
 *   • Check (mark in mail → Finance posts)     — DB-side only.
 *
 * The currently active method is highlighted in gold with an ACTIVE
 * pill. PM autopay-failure prompts render inline above the tabs so
 * the re-enroll CTA is always visible on the same panel.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Button } from '../ui/button';
import { Card } from '../ui/card';
import { AlertTriangle, Building2, CheckCircle2, CreditCard,
         FileText, Loader2, RefreshCw, Zap } from 'lucide-react';
import StripeCardForm from './StripeCardForm';

const API = process.env.REACT_APP_BACKEND_URL;

async function ensureStripeJs() {
  if (window.Stripe) return true;
  return new Promise((resolve, reject) => {
    const s = document.createElement('script');
    s.src = 'https://js.stripe.com/v3/';
    s.onload = () => resolve(true);
    s.onerror = reject;
    document.body.appendChild(s);
  });
}

const METHODS = [
  { key: 'ach',     label: 'ACH Direct Debit',    icon: Building2,  accent: '#3b82f6', sub: 'Recurring · Stripe-hosted' },
  { key: 'card',    label: 'Credit / Debit Card', icon: CreditCard, accent: '#8b5cf6', sub: 'Recurring · In-app secure form' },
  { key: 'ach_once', label: 'ACH One-Time',       icon: Building2,  accent: '#0ea5e9', sub: 'Single transfer' },
  { key: 'check',   label: 'Pay by Check',        icon: FileText,   accent: '#64748b', sub: 'Mail · Finance posts receipt' },
];

export default function PaymentMethodsPanel({ property_id, customer_id, customer_email }) {
  const [snap, setSnap] = useState(null);
  const [notes, setNotes] = useState([]);
  const [active, setActiveRaw] = useState(null); // which tab is open
  const [busy, setBusy] = useState('');
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');
  const [checkForm, setCheckForm] = useState({ check_number: '', invoice_number: '' });

  // Iter-168 · Phase 2 — persist the last-tapped payment method tab
  // per-property so PMs land back where they left off when they
  // navigate away and return to the Billing and A/R board.
  const persistKey = property_id ? `necs.pm.paymentTab:${property_id}` : null;
  const setActive = useCallback((k) => {
    setActiveRaw(k);
    try {
      if (persistKey) {
        if (k) sessionStorage.setItem(persistKey, k);
        else sessionStorage.removeItem(persistKey);
      }
    } catch (_) {}
  }, [persistKey]);

  const load = useCallback(async () => {
    if (!property_id) return;
    try {
      const [s, n] = await Promise.all([
        fetch(`${API}/api/iter156/autopay/status/${encodeURIComponent(property_id)}`,
              { credentials: 'include' }).then((r) => r.ok ? r.json() : null),
        fetch(`${API}/api/iter156/autopay/pm-notifications/${encodeURIComponent(property_id)}`,
              { credentials: 'include' }).then((r) => r.ok ? r.json() : { notifications: [] }),
      ]);
      setSnap(s);
      setNotes(n.notifications || []);
    } catch (_) {}
  }, [property_id]);
  useEffect(() => { load(); }, [load]);

  const activeKind = useMemo(() => {
    if (!snap?.autopay_active) return null;
    return snap.payment_method_kind === 'card' ? 'card' : 'ach';
  }, [snap]);

  useEffect(() => {
    // Priority order for landing tab:
    //   1. Currently enrolled method (activeKind) — always overrides
    //      stored state so PMs see their live status first.
    //   2. Iter-168 · Phase 2 — the tab the PM last opened for this
    //      property (sessionStorage).
    //   3. Nothing (keep all four unopened).
    if (active !== null) return;
    if (activeKind) { setActiveRaw(activeKind); return; }
    try {
      const stored = persistKey ? sessionStorage.getItem(persistKey) : null;
      if (stored && ['ach', 'card', 'ach_once', 'check'].includes(stored)) {
        setActiveRaw(stored);
      }
    } catch (_) {}
  }, [activeKind, active, persistKey]);

  const enrollAch = async () => {
    setBusy('ach'); setMsg(''); setErr('');
    try {
      const r = await fetch(`${API}/api/iter156/autopay/init`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ property_id, customer_id, customer_email }),
      });
      const d = await r.json();
      if (d.status !== 'ready') {
        setErr(d.note || (d.status === 'stripe_error'
                          ? `ACH setup couldn't start (${d.step}).`
                          : 'ACH setup temporarily unavailable.'));
        setBusy(''); return;
      }
      const pubKey = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;
      if (!pubKey) {
        setErr('Stripe publishable key not configured. Add REACT_APP_STRIPE_PUBLISHABLE_KEY.');
        setBusy(''); return;
      }
      await ensureStripeJs();
      const stripe = window.Stripe(pubKey);
      const collect = await stripe.collectBankAccountForSetup({
        clientSecret: d.client_secret,
        params: {
          payment_method_type: 'us_bank_account',
          payment_method_data: {
            billing_details: { name: customer_id, email: customer_email || '' },
          },
        },
      });
      if (collect.error) throw new Error(collect.error.message);
      const confirm = await stripe.confirmUsBankAccountSetup(d.client_secret);
      if (confirm.error) throw new Error(confirm.error.message);
      const cr = await fetch(`${API}/api/iter156/autopay/confirm`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ property_id, setup_intent_id: confirm.setupIntent.id }),
      });
      const cd = await cr.json();
      if (cd.status === 'active') {
        setMsg(`ACH autopay active — bank ****${cd.bank_last4}. Next payment ${(cd.next_payment_at || '').slice(0, 10)}.`);
      } else {
        setMsg(cd.message || 'ACH verification pending.');
      }
      await load();
    } catch (e) { setErr(String(e.message || e).slice(0, 200)); }
    setBusy('');
  };

  const oneTimeAch = async () => {
    setBusy('ach_once'); setMsg(''); setErr('');
    try {
      const raw = window.prompt('Enter one-time ACH amount (USD):', '250');
      const amount = Number(raw);
      if (!raw || Number.isNaN(amount) || amount <= 0) { setBusy(''); return; }
      const r = await fetch(`${API}/api/iter156/payment-methods/ach-one-time/init`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ property_id, amount, customer_email }),
      });
      const d = await r.json();
      if (d.status !== 'ready') {
        setErr(d.note || (d.status === 'stripe_error'
                          ? `One-time ACH couldn't start (${d.step}).`
                          : 'ACH one-time temporarily unavailable.'));
        setBusy(''); return;
      }
      setMsg('One-time ACH prepared. Complete the bank verification on Stripe, then click Refresh.');
    } catch (e) { setErr(String(e.message || e).slice(0, 200)); }
    setBusy('');
  };

  const markCheckMailed = async () => {
    if (!checkForm.invoice_number) { setErr('Enter an invoice number.'); return; }
    setBusy('check'); setMsg(''); setErr('');
    try {
      const r = await fetch(`${API}/api/iter156/payment-methods/check/mark-processing`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ property_id,
                               invoice_number: checkForm.invoice_number,
                               check_number: checkForm.check_number }),
      });
      const d = await r.json();
      if (d.status === 'processing') {
        setMsg(`Check marked as processing for invoice ${d.invoice_number}. Finance will post receipt.`);
        setCheckForm({ check_number: '', invoice_number: '' });
      } else setErr('Check status update failed.');
    } catch (e) { setErr(String(e.message || e).slice(0, 200)); }
    setBusy('');
  };

  const status = snap?.autopay_status;

  return (
    <Card className="p-4 bg-[#141414] border-zinc-800 space-y-4"
          data-testid={`payment-methods-panel-${property_id}`}>
      {/* Header + refresh + summary line */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <p className="text-[10px] uppercase tracking-widest text-zinc-400">Payment Methods</p>
          <p className="text-sm text-white mt-1" data-testid="pm-status-summary">
            {snap?.autopay_active && (
              <>
                <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-blue-500 text-white mr-2">
                  AUTOPAY ACTIVE
                </span>
                {snap.payment_method_kind === 'card'
                  ? `Card ${(snap.card_brand || '').toUpperCase()} ****${snap.card_last4} · Next ${(snap.next_payment_at || '').slice(0, 10)}`
                  : `Bank ****${snap.bank_last4} · Next ${(snap.next_payment_at || '').slice(0, 10)}`}
              </>
            )}
            {status === 'RETRY_PENDING' && (
              <>
                <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-orange-500 text-white mr-2">RETRY IN 24H</span>
                <span className="text-orange-300">First autopay attempt failed. Automatic retry scheduled.</span>
              </>
            )}
            {status === 'FAILED' && (
              <>
                <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-orange-600 text-white mr-2">AUTOPAY FAILED</span>
                <span className="text-orange-300">Both attempts failed. Please re-enroll below.</span>
              </>
            )}
            {!snap?.autopay_active && status !== 'RETRY_PENDING' && status !== 'FAILED' && (
              <>
                <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-zinc-700 text-zinc-300 mr-2">NO AUTOPAY</span>
                <span className="text-zinc-400">Pick any of the four methods below.</span>
              </>
            )}
          </p>
        </div>
        <Button size="sm" onClick={load}
                className="text-[10px] uppercase"
                style={{ backgroundColor: '#27272a', color: '#fff' }}
                data-testid="pm-refresh-status">
          <RefreshCw className="h-3 w-3 mr-1" /> Refresh
        </Button>
      </div>

      {/* PM notifications strip (autopay failed / grace expiring, etc.). */}
      {notes.length > 0 && (
        <div className="rounded border border-orange-500/40 bg-orange-500/10 p-3"
             data-testid="pm-notifications-strip">
          <p className="text-[10px] uppercase tracking-widest text-orange-400 flex items-center gap-1">
            <AlertTriangle className="h-3 w-3" /> Attention
          </p>
          {notes.slice(0, 3).map((n, i) => (
            <p key={i} className="text-xs mt-1 text-orange-200" data-testid={`pm-notif-${i}`}>
              {n.message || n.type}
            </p>
          ))}
        </div>
      )}

      {/* Iter-166 · Phase 3 + Iter-167 · Phase 3 — Empty state
          illustration + callout + Set Up Autopay CTA rendered ONLY
          when no autopay method is enrolled. Once the PM enrolls
          (activeKind flips to ach/card) this block disappears and
          the enrolled method status shown above takes over. */}
      {!activeKind && (
        <div className="rounded-xl border p-5 space-y-4"
             style={{ backgroundColor: '#0b1e2e', borderColor: '#1e40af' }}
             data-testid="pm-payments-empty-state">
          <div className="flex items-center gap-4">
            <div className="shrink-0 w-16 h-16 rounded-full flex items-center justify-center relative"
                 style={{ backgroundColor: '#1e3a8a' }}
                 data-testid="pm-payments-empty-illustration">
              <Zap className="h-8 w-8" style={{ color: '#facc15' }} />
              <CheckCircle2 className="h-4 w-4 absolute -bottom-1 -right-1"
                            style={{ color: '#10b981', backgroundColor: '#0b1e2e', borderRadius: '9999px' }} />
            </div>
            <div className="flex-1">
              <p className="text-[10px] uppercase tracking-widest text-blue-300 font-bold">
                Save Time · Skip Manual Payments
              </p>
              <p className="text-sm text-white mt-1" data-testid="pm-payments-empty-callout">
                Save 10 minutes a month by enrolling once — your invoices pay themselves on the due date.
              </p>
            </div>
          </div>
          <Button
            onClick={() => {
              // Iter-167 · Phase 3 — log the click through the same
              // funnel analytics endpoint the Recommended card uses.
              fetch(`${API}/api/analytics/autopay-event`, {
                method: 'POST', credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ event_type: 'click_autopay_cta', property_id }),
              }).catch(() => {});
              setActive('ach');
            }}
            className="w-full sm:w-auto text-xs uppercase font-bold tracking-widest px-6 py-2.5"
            style={{ backgroundColor: '#3b82f6', color: '#fff' }}
            data-testid="pm-payments-empty-cta"
          >
            <Zap className="h-3.5 w-3.5 mr-1.5" /> Set Up Autopay
          </Button>
        </div>
      )}

      {/* Tab strip — all four methods equally presented. */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-2" data-testid="pm-methods-tabs">
        {METHODS.map((m) => {
          const isActiveTab = active === m.key;
          const isEnrolled = activeKind === m.key;
          return (
            <button
              key={m.key}
              onClick={() => setActive(m.key)}
              className={`text-left rounded-lg p-3 border transition-all`}
              style={{
                backgroundColor: isActiveTab ? '#0f0f0f' : '#141414',
                borderColor: isEnrolled ? '#C9A962' : (isActiveTab ? m.accent : '#27272a'),
                boxShadow: isEnrolled ? '0 0 0 1px #C9A962 inset' : 'none',
              }}
              data-testid={`pm-method-tab-${m.key}`}
            >
              <div className="flex items-center gap-2">
                <m.icon className="h-4 w-4" style={{ color: m.accent }} />
                <span className="text-xs font-bold uppercase tracking-widest text-white">{m.label}</span>
                {isEnrolled && (
                  <span className="ml-auto px-1.5 py-0.5 rounded-full text-[9px] font-bold uppercase"
                        style={{ backgroundColor: '#C9A962', color: '#111' }}
                        data-testid={`pm-method-active-pill-${m.key}`}>
                    Active
                  </span>
                )}
              </div>
              <p className="text-[10px] text-zinc-400 mt-1">{m.sub}</p>
            </button>
          );
        })}
      </div>

      {/* Body — renders the section for the selected tab. */}
      {active === 'ach' && (
        <div className="rounded border border-blue-500/30 bg-blue-500/5 p-4 space-y-3"
             data-testid="pm-section-ach">
          <p className="text-[10px] uppercase tracking-widest text-blue-300">ACH Direct Debit</p>
          <p className="text-xs text-zinc-300">
            Enroll a US bank account for recurring autopay. Stripe will collect + verify the account.
          </p>
          <Button onClick={enrollAch} disabled={busy === 'ach'}
                  className="text-[11px] uppercase font-bold tracking-widest"
                  style={{ backgroundColor: '#3b82f6', color: '#fff' }}
                  data-testid="pm-method-ach-enroll">
            {busy === 'ach' ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Building2 className="h-3 w-3 mr-1" />}
            {activeKind === 'ach' ? 'Re-enroll ACH Autopay' : 'Enroll ACH Autopay'}
          </Button>
        </div>
      )}

      {active === 'card' && (
        <div className="rounded border border-violet-500/30 bg-violet-500/5 p-4 space-y-3"
             data-testid="pm-section-card">
          <p className="text-[10px] uppercase tracking-widest text-violet-300">Credit / Debit Card</p>
          <p className="text-xs text-zinc-300">
            Enter your card once. NECS never sees the card number — Stripe handles it end-to-end.
            Your card is charged on each invoice due date.
          </p>
          <StripeCardForm
            property_id={property_id}
            customer_id={customer_id}
            customer_email={customer_email}
            onEnrolled={() => { load(); }}
          />
        </div>
      )}

      {active === 'ach_once' && (
        <div className="rounded border border-sky-500/30 bg-sky-500/5 p-4 space-y-3"
             data-testid="pm-section-ach-once">
          <p className="text-[10px] uppercase tracking-widest text-sky-300">ACH One-Time Transfer</p>
          <p className="text-xs text-zinc-300">
            Pay a single invoice from your bank without enrolling in autopay. Best for one-off balances.
          </p>
          <Button onClick={oneTimeAch} disabled={busy === 'ach_once'}
                  className="text-[11px] uppercase font-bold tracking-widest"
                  style={{ backgroundColor: '#0ea5e9', color: '#fff' }}
                  data-testid="pm-method-ach-once-start">
            {busy === 'ach_once' ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Building2 className="h-3 w-3 mr-1" />}
            Start one-time ACH
          </Button>
        </div>
      )}

      {active === 'check' && (
        <div className="rounded border border-slate-500/40 bg-slate-500/10 p-4 space-y-3"
             data-testid="pm-section-check">
          <p className="text-[10px] uppercase tracking-widest text-slate-300">Pay by Check</p>
          <p className="text-xs text-zinc-300">
            Mailing a check? Enter the invoice number and check number below. Your invoice will show
            <span className="mx-1 px-1 rounded bg-blue-500 text-white text-[10px] font-bold">CHECK PROCESSING</span>
            until Finance posts the receipt.
          </p>
          <div className="grid sm:grid-cols-2 gap-2">
            <input
              type="text" value={checkForm.invoice_number}
              onChange={(e) => setCheckForm({ ...checkForm, invoice_number: e.target.value })}
              placeholder="Invoice number"
              className="rounded bg-zinc-900 border border-zinc-700 text-white text-xs p-2"
              data-testid="pm-check-invoice"
            />
            <input
              type="text" value={checkForm.check_number}
              onChange={(e) => setCheckForm({ ...checkForm, check_number: e.target.value })}
              placeholder="Check number (optional)"
              className="rounded bg-zinc-900 border border-zinc-700 text-white text-xs p-2"
              data-testid="pm-check-number"
            />
          </div>
          <Button onClick={markCheckMailed} disabled={busy === 'check' || !checkForm.invoice_number}
                  className="text-[11px] uppercase font-bold tracking-widest"
                  style={{ backgroundColor: '#10b981', color: '#fff' }}
                  data-testid="pm-check-submit">
            {busy === 'check' ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <CheckCircle2 className="h-3 w-3 mr-1" />}
            Mark check in mail
          </Button>
        </div>
      )}

      {msg && <p className="text-xs" style={{ color: '#7CFC8A' }} data-testid="pm-msg">{msg}</p>}
      {err && <p className="text-xs text-red-400" data-testid="pm-err">{err}</p>}
    </Card>
  );
}
